import React, { useCallback, useEffect, useRef, useState } from "react";
import Viewport3D from "@/components/Viewport3D";
import { useStudio } from "@/store/useStudio";
import { kbApi } from "@/api/kb";
import { fileToSource } from "@/lib/dsymbia";
import { warn } from "@/lib/log";

// Map a location id → the 3D overlay bucket (mirrors App.js).
const LOC_TO_OVERLAY = {
  rooftop: "rooftop",
  ground: "ground",
  indoor: "interior",
  balcony: "facade",
  facade_s: "facade",
  facade_e: "facade",
  facade_w: "facade",
  facade_n: "facade",
};

function base64ToFile(b64, name) {
  const binary = atob(b64);
  const len = binary.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i += 1) bytes[i] = binary.charCodeAt(i);
  return new File([bytes], name || "model.3dm", { type: "application/octet-stream" });
}

// Embedded 3D viewport — mounted in a React Native WebView on mobile. Reuses the web app's
// proven Three.js / rhino3dm engine and speaks to the native shell over postMessage.
export default function ViewportEmbed() {
  const setKb = useStudio((s) => s.setKb);
  const setGeo = useStudio((s) => s.setGeo);
  const setWeather = useStudio((s) => s.setWeather);
  const model = useStudio((s) => s.model);
  const setModel = useStudio((s) => s.setModel);
  const setSourceFile = useStudio((s) => s.setSourceFile);

  const [modelFile, setModelFile] = useState(null);
  const [useSample, setUseSample] = useState(false);
  const [highlights, setHighlights] = useState([]);
  const [areaFocusSystem, setAreaFocusSystem] = useState(null);
  const [faceScores, setFaceScores] = useState(null);

  const readyRef = useRef(false);
  const gotContextRef = useRef(false);

  // Compact chrome for small (mobile) canvases so the toolbars never smother the 3D model.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const compact = new URLSearchParams(window.location.search).get("compact") === "1";
    if (!compact) return;
    const el = document.createElement("style");
    el.setAttribute("data-embed-compact", "1");
    el.textContent = `
      /* Native shell renders the tool controls OUTSIDE the canvas — hide the in-canvas ones. */
      [data-testid="analysis-toggle"],[data-testid="measure-toggle"],[data-testid="section-toggle"],
      [data-testid="measure-panel"],[data-testid="section-panel"],[data-testid="analysis-flexbar"],
      [data-testid="model-filebar"]{display:none!important;}
      [data-testid="layer-panel"]{transform:scale(.78)!important;transform-origin:bottom left;left:6px!important;bottom:6px!important;}
      [data-testid="surface-hover-badge"],[data-testid="surface-pick-banner"]{font-size:10.5px!important;padding:5px 10px!important;}
    `;
    document.head.appendChild(el);
    return () => { el.remove(); };
  }, []);

  const post = useCallback((msg) => {
    try {
      const s = JSON.stringify(msg);
      if (window.ReactNativeWebView?.postMessage) window.ReactNativeWebView.postMessage(s);
      else if (window.parent && window.parent !== window) window.parent.postMessage(s, "*");
    } catch (e) {
      warn("embed post failed", e);
    }
  }, []);

  // Seed the shared knowledge base (drives fixed-footprint systems & analysis math).
  useEffect(() => {
    kbApi.bootstrap().then(setKb).catch((e) => warn("KB bootstrap failed", e));
  }, [setKb]);

  // Surface the parsed building dimensions to the native Building-Specs overlay.
  useEffect(() => {
    post({ type: "specs", status: model.status, name: model.name, dims: model.dims, error: model.error });
  }, [model, post]);

  // Report the live measure readout up to the native measure panel.
  const measureInfo = useStudio((s) => s.measureInfo);
  useEffect(() => { post({ type: "measureInfo", dist: measureInfo?.dist ?? null }); }, [measureInfo, post]);

  // Bridge the model's computed gross face areas up so the native shell can derive the site's
  // Viable Area (Dynamic Opportunity) and drive the 3-tier Allocation Funnel.
  const grossAreas = useStudio((s) => s.grossAreas);
  useEffect(() => { if (grossAreas) post({ type: "gross", gross: grossAreas }); }, [grossAreas, post]);

  const applyLocations = useCallback((locationIds) => {
    const overlays = [...new Set((locationIds || []).map((l) => LOC_TO_OVERLAY[l]).filter(Boolean))];
    setHighlights(overlays);
  }, []);

  const handleMessage = useCallback((raw) => {
    let d;
    try { d = typeof raw === "string" ? JSON.parse(raw) : raw; } catch { return; }
    if (!d || !d.type) return;
    if (["init", "site", "locations", "areaFocus", "faceScores", "loadModel", "useSample", "clearModel"].includes(d.type)) {
      gotContextRef.current = true;
    }
    switch (d.type) {
      case "init":
      case "site":
        if (d.geo) setGeo(d.geo);
        if (d.weather) setWeather(d.weather);
        if (Array.isArray(d.locations)) applyLocations(d.locations);
        if ("areaFocusSystem" in d) setAreaFocusSystem(d.areaFocusSystem || null);
        if (d.faceScores) setFaceScores(d.faceScores);
        if (d.model === "sample") { setModelFile(null); setUseSample(true); }
        break;
      case "locations":
        applyLocations(d.locations || []);
        break;
      case "areaFocus":
        setAreaFocusSystem(d.systemId || null);
        break;
      case "faceScores":
        setFaceScores(d.scores || null);
        break;
      case "loadModel": {
        try {
          const f = base64ToFile(d.dataBase64, d.filename);
          setUseSample(false);
          setModelFile(f);
          fileToSource(f).then(setSourceFile).catch((e) => warn("source capture failed", e));
        } catch (e) { warn("loadModel failed", e); }
        break;
      }
      case "useSample":
        setModelFile(null); setUseSample(true); setSourceFile(null);
        break;
      case "clearModel":
        setModelFile(null); setUseSample(false); setSourceFile(null);
        break;
      // ---- Tool controls driven from the native shell (rendered outside the 3D canvas) ----
      case "toolMeasure":
        useStudio.getState().setMeasureActive(!!d.on);
        break;
      case "measureClear":
        useStudio.getState().clearMeasureNow();
        break;
      case "toolSection":
        useStudio.getState().setSectionOn(!!d.on);
        break;
      case "sectionAxis":
        if (d.axis) useStudio.getState().setSectionXYZ((s) => ({ ...s, [d.axis]: Number(d.value) }));
        break;
      case "sectionReset":
        useStudio.getState().setSectionXYZ({ x: 1, y: 1, z: 1 });
        break;
      case "toolAnalysis":
        useStudio.getState().setAnalysisMode(!!d.on);
        break;
      case "analysisSeason":
        if (d.value) useStudio.getState().setSeason(d.value);
        break;
      case "analysisDli":
        useStudio.getState().setDliThreshold(Number(d.value));
        break;
      case "analysisArea":
        useStudio.getState().setAreaThreshold(Number(d.value));
        break;
      case "allocDraw":
        useStudio.getState().setAllocDraw(d.on ? "mobile" : null);
        break;
      // ---- Classify surfaces (moved to the native tools row on mobile) ----
      case "classify":
        useStudio.getState().setClassifyMode(!!d.on);
        break;
      case "classifyCategory":
        if (d.value) useStudio.getState().setClassifyCategory(d.value);
        break;
      case "classifySubdivide":
        useStudio.getState().setClassifySubdivide(!!d.on);
        break;
      default:
        break;
    }
  }, [setGeo, setWeather, setSourceFile, applyLocations]);

  useEffect(() => {
    const winHandler = (e) => handleMessage(e.data);
    // RN WebView delivers postMessage on window (Android) and document (iOS).
    window.addEventListener("message", winHandler);
    document.addEventListener("message", winHandler);
    if (!readyRef.current) { readyRef.current = true; post({ type: "ready" }); }
    // Standalone / web-preview fallback: if no native shell drives us shortly, show the sample.
    const fallback = setTimeout(() => {
      if (!gotContextRef.current) { setUseSample(true); }
    }, 1600);
    return () => {
      clearTimeout(fallback);
      window.removeEventListener("message", winHandler);
      document.removeEventListener("message", winHandler);
    };
  }, [handleMessage, post]);

  const onPickModel = useCallback((file) => {
    setUseSample(false);
    setModelFile(file);
    fileToSource(file).then(setSourceFile).catch((e) => warn("source capture failed", e));
  }, [setSourceFile]);
  const onUseSample = useCallback(() => { setModelFile(null); setUseSample(true); setSourceFile(null); }, [setSourceFile]);
  const onClear = useCallback(() => { setModelFile(null); setUseSample(false); setSourceFile(null); }, [setSourceFile]);

  const onSurfaceArea = useCallback((area) => post({ type: "surfaceArea", area }), [post]);
  const onSurfaceTag = useCallback((t) => post({ type: "surfaceTag", ...t }), [post]);
  const onNotify = useCallback((message) => post({ type: "notify", message }), [post]);
  const onAllocArea = useCallback((area) => post({ type: "allocArea", area }), [post]);

  return (
    <div style={{ position: "fixed", inset: 0, background: "linear-gradient(155deg, #E8ECEF 0%, #F8FAFC 100%)" }}>
      <Viewport3D
        modelFile={modelFile}
        useSample={useSample}
        highlights={highlights}
        model={model}
        setModel={setModel}
        embedded
        areaFocusSystem={areaFocusSystem}
        faceScores={faceScores}
        onSurfaceArea={onSurfaceArea}
        onPickModel={onPickModel}
        onUseSample={onUseSample}
        onClear={onClear}
        onSurfaceTag={onSurfaceTag}
        onNotify={onNotify}
        onAllocArea={onAllocArea}
      />
    </div>
  );
}
